import { inject, Injectable } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ENV } from 'src/environments/environment.provider';
import { datadogRum } from '@datadog/browser-rum';
import { APP_CONFIG } from '../config/settings';
import { IdentityService } from './identity.service';
import { formatAppVersion } from '../shared/services/version-checker.service';

@UntilDestroy()
@Injectable({
    providedIn: 'root'
})
export class UserMonitoringService {
    private readonly environment = inject(ENV);
    private readonly settings = inject(APP_CONFIG);
    private readonly identityService = inject(IdentityService);

    init(): void {
        if (this.environment.production !== true && this.settings.Datadog_Client_TokenRum === 'fake') {
            return;
        }

        datadogRum.init({
            applicationId: this.settings.Datadog_ApplicationId,
            clientToken: this.settings.Datadog_Client_TokenRum,
            site: 'us3.datadoghq.com',
            service: this.settings.Datadog_Service,
            env: this.settings.env,
            version: formatAppVersion(this.environment.appVersion, true),
            sessionSampleRate: 100,
            sessionReplaySampleRate: 100,
            trackUserInteractions: true,
            defaultPrivacyLevel: 'mask-user-input',
            trackResources: true,
            trackLongTasks: true,
            allowedTracingUrls: [
                'https:'.concat(this.settings.coreAPIUrl.substring(0, this.settings.coreAPIUrl.length - 1))
            ],
            beforeSend: (event) => {
                event.view.url = event.view.url.replace('/#', '');
                return true;
            }
        });
        datadogRum.startSessionReplayRecording();

        this.identityService.identity$.pipe(untilDestroyed(this)).subscribe((identity) => {
            if (identity) {
                datadogRum.setUser({
                    id: identity.userId,
                    name: identity.fullName,
                    email: identity.email
                });
            } else {
                datadogRum.clearUser();
            }
        });
    }

    addRumAction(eventName: string, context?: Record<string, any>): void {
        datadogRum.addAction(eventName, context);
    }
}
